import { getWebOrigin } from '@lib/platform';
import type { LivePlace } from '@models/api';
import { getCommunityPath, getEventPath } from '@utils/slugHelpers';

/**
 * The live poll's links, built by the client from the app's public web origin (CONTRACT § 4.15, § 10 N28):
 * the origin today's share links use (`getWebOrigin`), so a native build never puts `capacitor://` or
 * `https://localhost` under a QR. The backend returns only codes and keys.
 */
const origin = (): string => getWebOrigin().replace(/\/+$/, '');

/** The live link: what every QR encodes and the room types. */
export const liveLink = (code: string): string => `${origin()}/live/${encodeURIComponent(code)}`;

/** The screen link: display only for anyone but the author (TECH-867 § Words "Screen link"). */
export const screenLink = (code: string, key: string): string =>
  `${liveLink(code)}/screen?key=${encodeURIComponent(key)}`;

/** The host view (author only). */
export const hostLink = (code: string): string => `${liveLink(code)}/host`;

/** A link as the room types it: host and path, no scheme. */
export const linkDisplay = (link: string): string => link.replace(/^[a-z][a-z0-9+.-]*:\/\//i, '');

/** The typed link under every QR: host and path, no scheme ("app.meetball.fun/live/k7qx2fpa"). */
export const liveLinkDisplay = (code: string): string => linkDisplay(liveLink(code));

/**
 * The poll's place's own public page, or null when it has none: the server sends a slug only for a listed public
 * Space and a public Event (PR1), so an unlisted Space or a private Event never reaches a projector this way.
 */
export const placePageLink = ({ type, slug }: Pick<LivePlace, 'type' | 'slug'>): string | null => {
  if (!slug) return null;
  const page = { id: null, slug };
  return `${origin()}${type === 'event' ? getEventPath(page) : getCommunityPath(page)}`;
};
